import { businessService } from '@/pages/Business/services/business.service';
import { PaginatedRequest } from '@/types';
import type {
  ReturnsIntakeByDriverDetail,
  ReturnsIntakesByDriverResponse,
  ReturnsIntakeFilters,
  ValidateReturnReceptionArgs,
  ScannedReturnShipping,
  ProcessDriverComplianceArgs,
  ProcessDriverComplianceResponse,
  OpenReturnsSummaryResponse,
  DriversWithOpenReturnsResponse,
  PendingShippingsWithReturnsIntakeResponse,
  NotReportedShippingsWithReturnsIntakeResponse,
} from '../models';

export type GetReturnsIntakesByDriverArgs = {
  filters: ReturnsIntakeFilters;
  paginated: PaginatedRequest;
};

export type GetReturnsIntakeByDriverArgs = {
  driverId: string;
  closedAt?: string;
};

export type GetDriversWithOpenReturnsArgs = {
  filters: ReturnsIntakeFilters;
  paginated: PaginatedRequest;
};

const returnsIntakeApi = businessService.injectEndpoints({
  endpoints: (builder) => ({
    getReturnsIntakesByDriver: builder.query<
      ReturnsIntakesByDriverResponse,
      GetReturnsIntakesByDriverArgs
    >({
      query: ({ filters, paginated }) => ({
        url: '/returns-intakes/by-driver',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters.driverId && { driverId: filters.driverId }),
          ...(filters.commerceId && { commerceId: filters.commerceId }),
          ...(filters.returnsIntakeStatus && { status: filters.returnsIntakeStatus }),
          ...(filters.departmentId && { departmentId: filters.departmentId }),
          ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
          ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
        },
      }),
      providesTags: ['ShippingWithReturnsIntake'],
    }),
    getReturnsIntakeByDriver: builder.query<
      ReturnsIntakeByDriverDetail,
      GetReturnsIntakeByDriverArgs
    >({
      query: ({ driverId, closedAt }) => ({
        url: `/returns-intakes/by-driver/${driverId}`,
        params: {
          ...(closedAt && { closedAt }),
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ShippingWithReturnsIntake', id: arg.driverId },
      ],
    }),
    getOpenReturnShippingsByDriver: builder.query<
      ReturnsIntakeByDriverDetail,
      { driverId: string }
    >({
      query: ({ driverId }) => ({
        url: `/returns-intakes/drivers-with-open-returns/${driverId}`,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ShippingWithReturnsIntake', id: arg.driverId },
      ],
    }),
    validateReturnReception: builder.mutation<
      ScannedReturnShipping,
      ValidateReturnReceptionArgs
    >({
      query: (body) => ({
        url: '/returns-intakes/validate-return-reception',
        method: 'POST',
        body,
      }),
    }),
    processDriverCompliance: builder.mutation<
      ProcessDriverComplianceResponse,
      ProcessDriverComplianceArgs
    >({
      query: (body) => ({
        url: '/returns-intakes/process-driver-compliance',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ShippingWithReturnsIntake'],
    }),

    getOpenReturnsSummary: builder.query<
      OpenReturnsSummaryResponse,
      { driverId: string }
    >({
      query: ({ driverId }) => ({
        url: `/returns-intakes/driver/${driverId}/open`,
      }),
      providesTags: ['ShippingWithReturnsIntake'],
    }),
    closeReturnsIntakeByDriver: builder.mutation<
      { message: string },
      { driverId: string }
    >({
      query: (body) => ({
        url: '/returns-intakes/close-by-driver',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ShippingWithReturnsIntake'],
    }),
    getDriversWithOpenReturns: builder.query<
      DriversWithOpenReturnsResponse,
      GetDriversWithOpenReturnsArgs
    >({
      query: ({ filters, paginated }) => ({
        url: '/returns-intakes/drivers-with-open-returns',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters.driverId && { driverId: filters.driverId }),
          ...(filters.commerceId && { commerceId: filters.commerceId }),
          ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
          ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
        },
      }),
      providesTags: ['ShippingWithReturnsIntake'],
    }),

    getPendingShippingsWithReturnsIntakeList: builder.query<
      PendingShippingsWithReturnsIntakeResponse,
      GetDriversWithOpenReturnsArgs
    >({
      query: ({ filters, paginated }) => ({
        url: '/returns-intakes/pending-shippings-with-returns-intake',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters.driverId && { driverId: filters.driverId }),
          ...(filters.commerceId && { commerceId: filters.commerceId }),
          ...(filters.routeId && { routeId: filters.routeId }),
          ...(filters.departmentId && { departmentId: filters.departmentId }),
          ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
          ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
          ...(filters.shippingNumber && { shippingNumber: filters.shippingNumber }),
        },
      }),
      providesTags: ['ShippingWithReturnsIntake'],
    }),
    getNotReportedShippingsWithReturnsIntakeList: builder.query<
      NotReportedShippingsWithReturnsIntakeResponse,
      GetDriversWithOpenReturnsArgs
    >({
      query: ({ filters, paginated }) => ({
        url: '/returns-intakes/not-reported-shippings-with-returns-intake',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters.driverId && { driverId: filters.driverId }),
          ...(filters.commerceId && { commerceId: filters.commerceId }),
          ...(filters.routeId && { routeId: filters.routeId }),
          ...(filters.departmentId && { departmentId: filters.departmentId }),
          ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
          ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
          ...(filters.shippingNumber && { shippingNumber: filters.shippingNumber }),
        },
      }),
      providesTags: ['ShippingWithReturnsIntake'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetReturnsIntakesByDriverQuery,
  useGetReturnsIntakeByDriverQuery,
  useGetOpenReturnShippingsByDriverQuery,
  useValidateReturnReceptionMutation,
  useProcessDriverComplianceMutation,
  useGetOpenReturnsSummaryQuery,
  useCloseReturnsIntakeByDriverMutation,
  useGetDriversWithOpenReturnsQuery,
  useGetPendingShippingsWithReturnsIntakeListQuery,
  useGetNotReportedShippingsWithReturnsIntakeListQuery,
} = returnsIntakeApi;
